"use client";

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
};

export default function WhyDevrosSection() {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.1 });

  const benefits = [
    {
      icon: (
        <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      title: t('benefit1_title'),
      desc: t('benefit1_desc'),
    },
    {
      icon: (
        <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
        </svg>
      ),
      title: t('benefit2_title'),
      desc: t('benefit2_desc'),
    },
    {
      icon: (
        <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
        </svg>
      ),
      title: t('benefit3_title'),
      desc: t('benefit3_desc'),
    },
    {
      icon: (
        <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
        </svg>
      ),
      title: t('benefit4_title'),
      desc: t('benefit4_desc'),
    },
  ];

  const testimonials = [
    { quote: t('t1_quote'), name: t('t1_name'), role: t('t1_role'), metric: '2 workflows' },
    { quote: t('t2_quote'), name: t('t2_name'), role: t('t2_role'), metric: '4 workflows' },
    { quote: t('t3_quote'), name: t('t3_name'), role: t('t3_role'), metric: '8hrs → 0' },
  ];

  return (
    <section id="why-devros" ref={sectionRef} className="py-28 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(139,158,192,0.15)] to-transparent" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,145,46,0.04) 0%, transparent 70%)' }}
      />
      <div className="max-w-container mx-auto px-6">
        {/* Why Devros */}
        <div className="grid lg:grid-cols-2 gap-16 items-start mb-24">
          {/* Left */}
          <motion.div
            variants={fadeLeft}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            <div className="section-label mb-4">{t('why_label')}</div>
            <h2 className="font-heading text-headline font-bold text-white mb-6">
              {t('why_headline_1')}{' '}
              <span className="text-gradient-orange">{t('why_headline_accent')}</span>
              {' '}{t('why_headline_2')}
            </h2>
            <p className="text-muted text-lg leading-relaxed">{t('why_sub')}</p>
          </motion.div>

          {/* Right: Benefit cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefits.map((benefit, i) => (
              <motion.div
                key={benefit.title}
                variants={fadeUp}
                initial="hidden"
                animate={inView ? 'visible' : 'hidden'}
                custom={i + 1}
                className="card-glass card-hover-lift rounded-2xl p-6 group"
              >
                <div className="w-10 h-10 rounded-xl bg-[rgba(255,145,46,0.1)] border border-[rgba(255,145,46,0.15)] flex items-center justify-center text-primary mb-4 group-hover:bg-[rgba(255,145,46,0.18)] transition-all">
                  {benefit.icon}
                </div>
                <h4 className="font-heading text-base font-bold text-white mb-2">{benefit.title}</h4>
                <p className="text-muted text-sm leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Social Proof */}
        <div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            custom={0}
            className="text-center mb-12"
          >
            <div className="section-label mb-4">{t('social_label')}</div>
            <h2 className="font-heading text-title font-bold text-white mb-4">
              {t('social_headline_1')}{' '}
              <span className="text-gradient-orange">{t('social_headline_accent')}</span>
            </h2>
            <p className="text-muted">{t('social_sub')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={testimonial.name}
                variants={fadeUp}
                initial="hidden"
                animate={inView ? 'visible' : 'hidden'}
                custom={i + 1}
                className="card-glass card-hover-lift rounded-3xl p-7 flex flex-col justify-between group"
              >
                <div>
                  <div className="flex gap-1 mb-5">
                    {[...Array(5)].map((_, j) => (
                      <svg key={j} width="14" height="14" fill="#FF912E" viewBox="0 0 24 24">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                    ))}
                  </div>
                  <p className="text-white text-sm leading-relaxed mb-6 italic">"{testimonial.quote}"</p>
                </div>
                <div>
                  <div className="px-3 py-2 rounded-xl bg-[rgba(255,145,46,0.08)] border border-[rgba(255,145,46,0.15)] inline-block mb-4">
                    <span className="font-heading font-bold text-primary text-sm">{testimonial.metric}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-secondary flex items-center justify-center text-sm font-bold text-primary">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">{testimonial.name}</div>
                      <div className="text-[11px] text-muted">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}